import { useEffect, useRef } from 'react'

import { getAIConversation, stopAITurn } from '../../../../api'
import { EMPTY_FUNCTION, EMPTY_OBJECT } from '../../../../constants/common'
import {
  isAIConversationGenerationFailed,
  isAIConversationGenerationRunning,
  isAIStopTurnRequested,
} from '../../../../utils/aiAgents'
import { isApiNotFoundStatus } from '../../../../utils/api'
import { GretaAIChatStatus } from '../../GretaAIChat.constants'
import { useGretaAIChatPendingAnswerLabel } from '../useGretaAIChatPendingAnswerLabel'

export function useGretaAIChatGenerationWait({
  abortReloadMessages,
  activeChatId,
  clearTurn,
  getIsGenerationSettledRecently,
  getIsOpenConversation,
  getIsTimeoutStopPending,
  getIsTurnInFlight,
  pollUntilSettled,
  preserveOrStartTurn,
  reloadMessages,
  setStatus,
  settleTimeoutStop,
  turnControllersRef,
  onChatMissing = EMPTY_FUNCTION,
  onGenerationSettled = EMPTY_FUNCTION,
  onTurnSettled = EMPTY_FUNCTION,
}) {
  const cancelTurnsRef = useRef(new Map())

  // The stop owns the turn until it settles; `cancelTurnsRef` lingers until the next send.
  const stopsInFlightRef = useRef(new Set())

  // A timeout's stop must not reload over a turn queued behind it.
  const queuedTurnsRef = useRef(new Set())
  const cancelledQueuedTurnsRef = useRef(new Set())

  const {
    clearPendingAnswerLabel,
    pendingAnswerFileName,
    refreshPendingAnswerLabel,
  } = useGretaAIChatPendingAnswerLabel({ activeChatId, getIsOpenConversation })

  // `isQuiet`: wait without Thinking — the user already sees the timeout.
  function enterGenerationWait(
    conversationId,
    { isQuiet = false, pendingAnswers } = EMPTY_OBJECT
  ) {
    if (!isQuiet && getIsOpenConversation(conversationId)) {
      setStatus(GretaAIChatStatus.Thinking)
    }

    refreshPendingAnswerLabel(conversationId, pendingAnswers)

    return pollUntilSettled(conversationId)
  }

  // `isConfirmed`: false when the server never answered the stop, so the turn may still run.
  function settleGenerationWait(
    conversationId,
    { isConfirmed = true, silent = false } = EMPTY_OBJECT
  ) {
    clearPendingAnswerLabel(conversationId)
    clearTurn(conversationId)
    onGenerationSettled(conversationId, { isConfirmed })

    if (
      getIsOpenConversation(conversationId) &&
      !queuedTurnsRef.current.has(conversationId)
    ) {
      return reloadMessages(conversationId, { silent })
    }
  }

  async function handleStopSettled(
    conversationId,
    response,
    controller,
    { isTimeout = false } = EMPTY_OBJECT
  ) {
    if (isAIStopTurnRequested(response)) {
      await enterGenerationWait(conversationId, { isQuiet: isTimeout })
    }

    if (
      controller &&
      turnControllersRef.current.get(conversationId) === controller
    ) {
      turnControllersRef.current.delete(conversationId)
    }

    await settleGenerationWait(conversationId, {
      isConfirmed: Boolean(response),
      silent: isTimeout,
    })

    if (isTimeout) {
      settleTimeoutStop(conversationId)
    }
  }

  function cancelServerTurn(conversationId, controller, options) {
    if (conversationId) {
      stopsInFlightRef.current.add(conversationId)
      cancelTurnsRef.current.set(
        conversationId,
        stopAITurn({ conversationId })
          .then(response =>
            handleStopSettled(conversationId, response, controller, options)
          )
          .catch(() =>
            handleStopSettled(conversationId, null, controller, options)
          )
          .finally(() => stopsInFlightRef.current.delete(conversationId))
      )
    }
  }

  function getIsStopPending(conversationId) {
    return cancelTurnsRef.current.has(conversationId)
  }

  function getIsStopInFlight(conversationId) {
    return stopsInFlightRef.current.has(conversationId)
  }

  async function waitForPendingStop(conversationId) {
    const cancelTurns = cancelTurnsRef.current
    const queuedTurns = queuedTurnsRef.current

    if (getIsTimeoutStopPending(conversationId)) {
      queuedTurns.add(conversationId)
      // The stop's reload may already be in flight — it would land over the new question.
      abortReloadMessages()
    }

    await cancelTurns.get(conversationId)

    queuedTurns.delete(conversationId)
    cancelTurns.delete(conversationId)

    return !cancelledQueuedTurnsRef.current.delete(conversationId)
  }

  function cancelQueuedTurn(conversationId) {
    const isQueued = queuedTurnsRef.current.has(conversationId)

    if (isQueued) {
      cancelledQueuedTurnsRef.current.add(conversationId)
    }

    return isQueued
  }

  function checkForeignGeneration(conversationId) {
    return getAIConversation({ id: conversationId }).then(response => {
      if (
        isAIConversationGenerationRunning(response) &&
        getIsOpenConversation(conversationId) &&
        !getIsTurnInFlight(conversationId) &&
        !getIsStopInFlight(conversationId) &&
        !getIsGenerationSettledRecently(conversationId)
      ) {
        preserveOrStartTurn(conversationId)

        return enterGenerationWait(conversationId).then(() =>
          settleGenerationWait(conversationId)
        )
      }
    }, EMPTY_FUNCTION)
  }

  async function trackBackgroundGeneration(conversationId) {
    if (
      !getIsTurnInFlight(conversationId) &&
      !getIsStopInFlight(conversationId)
    ) {
      preserveOrStartTurn(conversationId)

      await pollUntilSettled(conversationId)

      const response = await getAIConversation({ id: conversationId }).catch(
        reason => {
          if (isApiNotFoundStatus(reason?.response?.status)) {
            return null
          }

          throw reason
        }
      )

      // The chat was deleted while generating in the background — drop it
      // from selection and the list so polling stops rediscovering it.
      if (!response) {
        clearPendingAnswerLabel(conversationId)
        clearTurn(conversationId)
        onChatMissing(conversationId)

        return
      }

      const delivered = !isAIConversationGenerationFailed(response)

      if (getIsOpenConversation(conversationId)) {
        await settleGenerationWait(conversationId)
      } else {
        clearPendingAnswerLabel(conversationId)
        clearTurn(conversationId)
        onGenerationSettled(conversationId)
      }

      onTurnSettled(conversationId, {
        delivered,
        isBackground: !getIsOpenConversation(conversationId),
      })
    }
  }

  useEffect(() => {
    if (activeChatId && !getIsTurnInFlight(activeChatId)) {
      checkForeignGeneration(activeChatId)
    }
  }, [activeChatId])

  return {
    cancelQueuedTurn,
    cancelServerTurn,
    clearPendingAnswerLabel,
    enterGenerationWait,
    getIsStopPending,
    pendingAnswerFileName,
    settleGenerationWait,
    trackBackgroundGeneration,
    waitForPendingStop,
  }
}
